import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Brush,
  Leaf,
  Wrench,
  Sparkles,
  Clock,
  MapPin,
  Loader2,
  X,
  Phone,
  CreditCard,
  Package,
  CheckCircle,
  FileText,
  Repeat,
  Trash2,
  Pause,
  Play
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const serviceIcons = {
  cleaning: { icon: Brush, color: 'bg-blue-100 text-blue-600' },
  garden: { icon: Leaf, color: 'bg-green-100 text-green-600' },
  repairs: { icon: Wrench, color: 'bg-orange-100 text-orange-600' },
  maintenance: { icon: Wrench, color: 'bg-yellow-100 text-yellow-600' },
  rental_prep: { icon: Sparkles, color: 'bg-purple-100 text-purple-600' },
  urgent: { icon: Phone, color: 'bg-red-100 text-red-600' },
};

const timeSlots = [
  { value: 'morning', label: 'morning' },
  { value: 'afternoon', label: 'afternoon' },
  { value: 'evening', label: 'evening' },
];

const ServicesPage = () => {
  const { language, isAdmin } = useAuth();
  const { t } = useTranslation(language);
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [serviceTypes, setServiceTypes] = useState({});
  const [packages, setPackages] = useState({});
  const [myPackages, setMyPackages] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [recurringBookings, setRecurringBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Minimum booking date is 3 days from now
  const minBookingDate = new Date();
  minBookingDate.setDate(minBookingDate.getDate() + 3);
  minBookingDate.setHours(0, 0, 0, 0);
  
  const [formData, setFormData] = useState({
    property_id: '',
    service_type: 'cleaning',
    package_type: 'single',
    description: '',
    scheduled_date: format(minBookingDate, 'yyyy-MM-dd'),
    time_slot: 'morning',
    hours_requested: 3,
  });

  const [recurringForm, setRecurringForm] = useState({
    property_id: '',
    service_type: 'cleaning',
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    time_slot: 'morning',
    hours_requested: 3,
    description: '',
  });

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, []);

  useEffect(() => {
    const type = searchParams.get('type');
    if (type && serviceTypes[type]) {
      setFormData(prev => ({ ...prev, service_type: type }));
      setDialogOpen(true);
    }
  }, [searchParams, serviceTypes]);

  const fetchData = async () => {
    try {
      const [servicesRes, propertiesRes, typesRes, packagesRes, myPackagesRes, blockedRes, invoicesRes, recurringRes] = await Promise.all([
        axios.get(`${API_URL}/services`),
        axios.get(`${API_URL}/properties`),
        axios.get(`${API_URL}/service-types`),
        axios.get(`${API_URL}/service-packages`),
        axios.get(`${API_URL}/packages/my`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/blocked-dates`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/invoices`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/recurring-bookings`).catch(() => ({ data: [] }))
      ]);
      setServices(servicesRes.data);
      setProperties(propertiesRes.data);
      setServiceTypes(typesRes.data);
      setPackages(packagesRes.data);
      setMyPackages(myPackagesRes.data);
      setBlockedDates(blockedRes.data.map(b => b.date));
      setInvoices(invoicesRes.data);
      setRecurringBookings(recurringRes.data);
      if (propertiesRes.data.length > 0 && !formData.property_id) {
        setFormData(prev => ({ ...prev, property_id: propertiesRes.data[0].id }));
      }
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  // Check if a date is disabled (before min date or blocked)
  const isDateDisabled = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Disabled if before minimum booking date
    if (date < minBookingDate) return true;
    // Disabled if in blocked dates
    if (blockedDates.includes(dateStr)) return true;
    return false;
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    const serviceId = searchParams.get('service_id');
    
    if (sessionId && paymentStatus === 'success') {
      try {
        if (serviceId) {
          // Direct service payment return
          const response = await axios.get(`${API_URL}/services/payment-status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            toast.success(language === 'fr' ? 'Paiement réussi ! Service réservé.' : 'Payment successful! Service booked.');
            fetchData();
          }
        } else {
          // Package purchase return
          const response = await axios.get(`${API_URL}/packages/payment-status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            toast.success(t('paymentSuccess'));
            fetchData();
          }
        }
      } catch (error) {
        console.error('Error checking payment:', error);
      }
    }
    
    if (paymentStatus === 'cancelled' && serviceId) {
      toast.error(language === 'fr' ? 'Paiement annulé. Le service n\'a pas été confirmé.' : 'Payment cancelled. Service was not confirmed.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner une propriété' : 'Please select a property');
      return;
    }
    
    // Check if selected date is valid
    const selectedDateObj = new Date(formData.scheduled_date);
    if (isDateDisabled(selectedDateObj)) {
      toast.error(language === 'fr' 
        ? 'Cette date n\'est pas disponible. Veuillez choisir une date au moins 3 jours à l\'avance.'
        : 'This date is not available. Please choose a date at least 3 days in advance.');
      return;
    }
    
    setSaving(true);
    
    try {
      if (totalRemainingHours >= formData.hours_requested) {
        // User has package hours → deduct from package
        await axios.post(`${API_URL}/services`, formData);
        toast.success(language === 'fr' 
          ? `Service réservé ! ${formData.hours_requested}h déduites de votre forfait.`
          : `Service booked! ${formData.hours_requested}h deducted from your package.`
        );
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        // No package hours → direct Stripe payment
        const response = await axios.post(`${API_URL}/services/direct-checkout`, formData);
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402) {
        toast.error(language === 'fr' ? detail?.message_fr : detail?.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : t('error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBuyPackage = async (packageType) => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/packages/purchase`, {
        package_type: packageType,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (serviceId) => {
    try {
      await axios.put(`${API_URL}/services/${serviceId}`, { status: 'cancelled' });
      toast.success(t('serviceCancelled'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    if (!recurringForm.property_id) {
      toast.error(language === 'fr' ? 'Sélectionnez une propriété' : 'Select a property');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/recurring-bookings`, recurringForm);
      toast.success(language === 'fr' ? 'Réservation récurrente créée !' : 'Recurring booking created!');
      setRecurringDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRecurring = async (id, isActive) => {
    try {
      await axios.put(`${API_URL}/recurring-bookings/${id}`, { is_active: !isActive });
      toast.success(language === 'fr' ? 'Mise à jour effectuée' : 'Updated');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteRecurring = async (id) => {
    try {
      await axios.delete(`${API_URL}/recurring-bookings/${id}`);
      toast.success(language === 'fr' ? 'Supprimé' : 'Deleted');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: properties.length > 0 ? properties[0].id : '',
      service_type: 'cleaning',
      package_type: 'single',
      description: '',
      scheduled_date: format(new Date(), 'yyyy-MM-dd'),
      time_slot: 'morning',
      hours_requested: 3,
    });
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setFormData(prev => ({ ...prev, scheduled_date: format(date, 'yyyy-MM-dd') }));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      accepted: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-error',
    };
    return badges[status] || 'badge-info';
  };

  const filteredServices = services.filter(service => {
    if (service.status === 'cancelled') return false; // Remove all cancelled
    if (activeTab === 'all') return true;
    return service.status === activeTab;
  });

  const calculateRevenue = (servicesList) => {
    return servicesList.reduce((sum, s) => {
      if (s.status === 'cancelled') return sum;
      const rate = serviceTypes[s.service_type]?.hourly_rate || 25;
      return sum + (rate * (s.hours_requested || 3));
    }, 0);
  };

  const totalRemainingHours = myPackages.reduce((sum, pkg) => sum + pkg.hours_remaining, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="services-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">{t('services')}</h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' 
              ? 'Planifiez et gérez vos services'
              : 'Schedule and manage your services'
            }
          </p>
        </div>
        <div className="flex gap-3">
          {!isAdmin && (
            <>
              <Button variant="outline" onClick={() => setPackageDialogOpen(true)} data-testid="buy-package-btn">
                <Package className="h-4 w-4 mr-2" />
                {t('packages')}
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="book-service-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('bookService')}
                  </Button>
                </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">{t('bookServiceTitle')}</DialogTitle>
                <DialogDescription>
                  {language === 'fr' 
                    ? 'Sélectionnez un service et une date'
                    : 'Select a service and date'
                  }
                </DialogDescription>
              </DialogHeader>
              
              {properties.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    {language === 'fr' 
                      ? 'Vous devez d\'abord ajouter une propriété'
                      : 'You need to add a property first'
                    }
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Service Type */}
                  <div className="space-y-3">
                    <Label>{t('serviceType')}</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(serviceTypes).filter(([key]) => key !== 'urgent').map(([key, service]) => {
                        const { icon: Icon, color } = serviceIcons[key] || { icon: Wrench, color: 'bg-gray-100 text-gray-600' };
                        const isSelected = formData.service_type === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setFormData({ ...formData, service_type: key })}
                            className={`p-3 rounded-xl border-2 transition-all ${
                              isSelected 
                                ? 'border-primary bg-primary/5' 
                                : 'border-border hover:border-primary/50'
                            }`}
                            data-testid={`service-type-${key}`}
                          >
                            <div className={`w-10 h-10 mx-auto rounded-lg ${color} flex items-center justify-center mb-2`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <span className="text-xs font-medium block truncate">
                              {language === 'fr' ? service.name_fr.split('/')[0] : service.name_en.split('/')[0]}
                            </span>
                            <span className="text-xs text-muted-foreground">{service.hourly_rate}€/h</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Property Selection */}
                  <div className="space-y-2">
                    <Label>{t('selectProperty')}</Label>
                    <Select 
                      value={formData.property_id} 
                      onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                    >
                      <SelectTrigger data-testid="property-select">
                        <SelectValue placeholder={t('selectProperty')} />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map((property) => (
                          <SelectItem key={property.id} value={property.id}>
                            {property.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Selection */}
                  <div className="space-y-2">
                    <Label>{t('selectDate')}</Label>
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' 
                        ? 'Réservation possible à partir de 3 jours'
                        : 'Booking available from 3 days ahead'}
                    </p>
                    <div className="border rounded-lg p-3">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        locale={language === 'fr' ? fr : undefined}
                        disabled={isDateDisabled}
                        fromDate={minBookingDate}
                        className="mx-auto"
                        data-testid="service-calendar"
                      />
                    </div>
                    {blockedDates.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {language === 'fr' 
                          ? `${blockedDates.length} date(s) non disponible(s)`
                          : `${blockedDates.length} date(s) unavailable`}
                      </p>
                    )}
                  </div>

                  {/* Time Slot */}
                  <div className="space-y-2">
                    <Label>{t('selectTime')}</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, time_slot: slot.value })}
                          className={`p-3 rounded-lg border-2 transition-all text-sm ${
                            formData.time_slot === slot.value 
                              ? 'border-primary bg-primary/5 text-primary' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`time-slot-${slot.value}`}
                        >
                          <Clock className="h-4 w-4 mx-auto mb-1" />
                          {t(slot.label)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label>{t('description')}</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={language === 'fr' ? 'Détails supplémentaires...' : 'Additional details...'}
                      rows={3}
                      data-testid="service-description-input"
                    />
                  </div>

                  <DialogFooter className="flex-col gap-3">
                    {/* Payment info */}
                    <div className="w-full text-sm">
                      {totalRemainingHours >= formData.hours_requested ? (
                        <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg" data-testid="hours-info">
                          <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                          <span>
                            {language === 'fr'
                              ? `${formData.hours_requested}h seront déduites de votre forfait (${totalRemainingHours}h disponibles)`
                              : `${formData.hours_requested}h will be deducted from your package (${totalRemainingHours}h available)`
                            }
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="direct-pay-info">
                          <CreditCard className="h-4 w-4 text-amber-600 shrink-0" />
                          <span className="text-amber-800">
                            {language === 'fr'
                              ? `Paiement direct: ${(serviceTypes[formData.service_type]?.hourly_rate || 25) * formData.hours_requested}€ (${formData.hours_requested}h x ${serviceTypes[formData.service_type]?.hourly_rate || 25}€/h)`
                              : `Direct payment: €${(serviceTypes[formData.service_type]?.hourly_rate || 25) * formData.hours_requested} (${formData.hours_requested}h x €${serviceTypes[formData.service_type]?.hourly_rate || 25}/h)`
                            }
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 justify-end w-full">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        {t('cancel')}
                      </Button>
                      <Button type="submit" className="btn-primary" disabled={saving} data-testid="submit-service-btn">
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : totalRemainingHours >= formData.hours_requested ? (
                          t('submit')
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4 mr-2" />
                            {language === 'fr' ? 'Payer et réserver' : 'Pay & Book'}
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Remaining Hours Card */}
      {!isAdmin && myPackages.length > 0 && (
        <Card className="card-base bg-primary/5 border-primary/20" data-testid="remaining-hours-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Package className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg text-foreground">{t('remainingHours')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'Heures disponibles dans vos forfaits' : 'Available hours in your packages'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-4xl font-bold text-primary">{totalRemainingHours}</span>
                <span className="text-lg text-muted-foreground ml-1">{language === 'fr' ? 'heures' : 'hours'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Urgent Call Banner - only for owners */}
      {!isAdmin && (
      <Card className="card-base bg-destructive/5 border-destructive/20" data-testid="urgent-banner">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <Phone className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="font-semibold text-foreground">{t('urgentCall')}</p>
                <p className="text-sm text-muted-foreground">{t('urgentPrice')}</p>
              </div>
            </div>
            <a 
              href="tel:0642526827"
              className="flex items-center gap-2 px-6 py-2 bg-destructive text-white rounded-full hover:bg-destructive/90 transition-colors font-medium"
              data-testid="call-urgent-btn"
            >
              <Phone className="h-4 w-4" />
              06 42 52 68 27
            </a>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Services Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6" data-testid="services-tabs">
          <TabsTrigger value="all">
            {language === 'fr' ? 'Tous' : 'All'} ({services.filter(s => s.status !== 'cancelled').length})
          </TabsTrigger>
          <TabsTrigger value="pending">
            {t('pending')} ({services.filter(s => s.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="accepted">
            {t('accepted')} ({services.filter(s => s.status === 'accepted').length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            {t('completed')} ({services.filter(s => s.status === 'completed').length})
          </TabsTrigger>
          {!isAdmin && (
            <TabsTrigger value="invoices" data-testid="invoices-tab">
              <FileText className="h-4 w-4 mr-1" />
              {language === 'fr' ? 'Factures' : 'Invoices'} ({invoices.length})
            </TabsTrigger>
          )}
          {!isAdmin && (
            <TabsTrigger value="recurring" data-testid="recurring-tab">
              <Repeat className="h-4 w-4 mr-1" />
              {language === 'fr' ? 'Récurrent' : 'Recurring'} ({recurringBookings.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Revenue Summary */}
        {['all', 'pending', 'accepted', 'completed'].includes(activeTab) && filteredServices.length > 0 && (
          <div className="mb-4 p-3 bg-primary/5 rounded-lg flex items-center gap-3" data-testid="tab-revenue">
            <CreditCard className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium text-muted-foreground">
              {isAdmin 
                ? (language === 'fr' ? 'Revenu total :' : 'Total Revenue:')
                : (language === 'fr' ? 'Total dépensé :' : 'Total Spent:')
              }
            </span>
            <span className="text-lg font-bold text-primary" data-testid="revenue-amount">
              {calculateRevenue(filteredServices)}€
            </span>
          </div>
        )}

        <TabsContent value={activeTab} className="space-y-4">
          {['all', 'pending', 'accepted', 'completed'].includes(activeTab) && (
          <>
          {filteredServices.length === 0 ? (
            <Card className="card-base">
              <CardContent className="py-16 text-center">
                <CalendarIcon className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">{t('noServices')}</h3>
                <Button className="btn-primary mt-4" onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('bookService')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredServices.map((service, index) => {
                const serviceInfo = serviceIcons[service.service_type] || { icon: CalendarIcon, color: 'bg-gray-100 text-gray-600' };
                const Icon = serviceInfo.icon;
                const property = properties.find(p => p.id === service.property_id);
                const typeInfo = serviceTypes[service.service_type];
                const rate = typeInfo?.hourly_rate || 25;
                const revenue = rate * (service.hours_requested || 3);
                
                return (
                  <Card 
                    key={service.id} 
                    className="card-base animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`service-card-${service.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl ${serviceInfo.color} flex items-center justify-center shrink-0`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="font-heading font-semibold text-foreground">
                                {language === 'fr' ? typeInfo?.name_fr : typeInfo?.name_en}
                              </h3>
                              {isAdmin && service.owner_name && (
                                <div className="flex items-center gap-2 mt-1 text-sm text-primary font-medium" data-testid={`service-owner-${service.id}`}>
                                  <span>{service.owner_name}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                <MapPin className="h-4 w-4" />
                                <span>{isAdmin && property?.address ? property.address : property?.name || 'Property'}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                <CalendarIcon className="h-4 w-4" />
                                <span>{format(new Date(service.scheduled_date), 'PPP', { locale: language === 'fr' ? fr : undefined })}</span>
                                <Clock className="h-4 w-4 ml-2" />
                                <span>{t(service.time_slot)}</span>
                              </div>
                              {service.description && (
                                <p className="mt-2 text-sm text-muted-foreground">{service.description}</p>
                              )}
                            </div>
                            
                            <div className="flex flex-col items-end gap-2">
                              <span className={getStatusBadge(service.status)}>
                                {t(service.status)}
                              </span>
                              <span className="text-lg font-bold text-primary" data-testid={`service-revenue-${service.id}`}>
                                {revenue}€
                              </span>
                              <span className="text-xs text-muted-foreground">{service.hours_requested || 3}h x {rate}€</span>
                              {!isAdmin && service.status === 'pending' && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="text-destructive hover:bg-destructive/10"
                                  onClick={() => handleCancel(service.id)}
                                  data-testid={`cancel-service-${service.id}`}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          </>
          )}
        </TabsContent>
      </Tabs>

      {/* Invoices Tab Content */}
      {activeTab === 'invoices' && (
        <div className="space-y-4 mt-4">
          {invoices.length === 0 ? (
            <Card className="card-base">
              <CardContent className="py-16 text-center">
                <FileText className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {language === 'fr' ? 'Aucune facture' : 'No invoices yet'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Les factures sont générées automatiquement quand un service est terminé.' : 'Invoices are auto-generated when a service is completed.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {invoices.map((inv) => {
                const typeInfo = serviceTypes[inv.service_type];
                return (
                  <Card key={inv.id} className="card-base" data-testid={`invoice-${inv.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <FileText className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold">{inv.invoice_number}</h3>
                            <p className="text-sm text-muted-foreground">
                              {language === 'fr' ? typeInfo?.name_fr : typeInfo?.name_en} - {inv.hours}h
                            </p>
                            <p className="text-xs text-muted-foreground">{inv.issued_date}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-foreground">{inv.total_amount}€</p>
                          <span className={inv.status === 'paid' ? 'badge-success' : 'badge-warning'}>
                            {inv.status === 'paid' ? (language === 'fr' ? 'Payée' : 'Paid') : (language === 'fr' ? 'Émise' : 'Issued')}
                          </span>
                          <p className="text-xs text-muted-foreground mt-1">
                            {inv.payment_method === 'package' ? (language === 'fr' ? 'Forfait' : 'Package') : (language === 'fr' ? 'Direct' : 'Direct')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Recurring Tab Content */}
      {activeTab === 'recurring' && (
        <div className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button className="btn-primary" onClick={() => setRecurringDialogOpen(true)} data-testid="create-recurring-btn">
              <Plus className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Nouveau récurrent' : 'New Recurring'}
            </Button>
          </div>

          {recurringBookings.length === 0 ? (
            <Card className="card-base">
              <CardContent className="py-16 text-center">
                <Repeat className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {language === 'fr' ? 'Aucune réservation récurrente' : 'No recurring bookings'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Créez des réservations automatiques hebdomadaires ou mensuelles.' : 'Set up automatic weekly or monthly bookings.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {recurringBookings.map((rb) => {
                const serviceInfo = serviceIcons[rb.service_type] || { icon: CalendarIcon, color: 'bg-gray-100 text-gray-600' };
                const Icon = serviceInfo.icon;
                const typeInfo = serviceTypes[rb.service_type];
                const property = properties.find(p => p.id === rb.property_id);
                const days = ['Lun/Mon', 'Mar/Tue', 'Mer/Wed', 'Jeu/Thu', 'Ven/Fri', 'Sam/Sat', 'Dim/Sun'];
                
                return (
                  <Card key={rb.id} className={`card-base ${!rb.is_active ? 'opacity-60' : ''}`} data-testid={`recurring-${rb.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl ${serviceInfo.color} flex items-center justify-center`}>
                            <Icon className="h-6 w-6" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold">
                              {language === 'fr' ? typeInfo?.name_fr : typeInfo?.name_en}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {property?.name} - {rb.hours_requested}h - {t(rb.time_slot)}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Repeat className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                {rb.frequency === 'weekly' 
                                  ? `${language === 'fr' ? 'Hebdo' : 'Weekly'} - ${days[rb.day_of_week] || ''}`
                                  : `${language === 'fr' ? 'Mensuel' : 'Monthly'} - ${language === 'fr' ? 'Jour' : 'Day'} ${rb.day_of_month}`
                                }
                              </span>
                              {rb.next_booking_date && (
                                <span className="text-xs text-primary font-medium">
                                  {language === 'fr' ? 'Prochain:' : 'Next:'} {rb.next_booking_date}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={rb.is_active ? 'badge-success' : 'badge-warning'}>
                            {rb.is_active ? (language === 'fr' ? 'Actif' : 'Active') : (language === 'fr' ? 'Pausé' : 'Paused')}
                          </span>
                          <Button variant="ghost" size="icon" onClick={() => handleToggleRecurring(rb.id, rb.is_active)} data-testid={`toggle-recurring-${rb.id}`}>
                            {rb.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteRecurring(rb.id)} data-testid={`delete-recurring-${rb.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Recurring Booking Dialog */}
      <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {language === 'fr' ? 'Nouvelle réservation récurrente' : 'New Recurring Booking'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' ? 'Planifiez un service automatique régulier' : 'Set up an automatic regular service'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRecurring} className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Propriété' : 'Property'}</Label>
              <Select value={recurringForm.property_id} onValueChange={(v) => setRecurringForm(p => ({ ...p, property_id: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service</Label>
              <Select value={recurringForm.service_type} onValueChange={(v) => setRecurringForm(p => ({ ...p, service_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(serviceTypes).filter(([k]) => k !== 'urgent').map(([key, s]) => (
                    <SelectItem key={key} value={key}>{language === 'fr' ? s.name_fr : s.name_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Fréquence' : 'Frequency'}</Label>
                <Select value={recurringForm.frequency} onValueChange={(v) => setRecurringForm(p => ({ ...p, frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">{language === 'fr' ? 'Hebdomadaire' : 'Weekly'}</SelectItem>
                    <SelectItem value="monthly">{language === 'fr' ? 'Mensuel' : 'Monthly'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {recurringForm.frequency === 'weekly' ? (
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Jour' : 'Day'}</Label>
                  <Select value={String(recurringForm.day_of_week)} onValueChange={(v) => setRecurringForm(p => ({ ...p, day_of_week: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[['0', 'Lundi/Monday'], ['1', 'Mardi/Tuesday'], ['2', 'Mercredi/Wednesday'], ['3', 'Jeudi/Thursday'], ['4', 'Vendredi/Friday']].map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Jour du mois' : 'Day of month'}</Label>
                  <Select value={String(recurringForm.day_of_month)} onValueChange={(v) => setRecurringForm(p => ({ ...p, day_of_month: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 28}, (_, i) => i + 1).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Créneau' : 'Time slot'}</Label>
                <Select value={recurringForm.time_slot} onValueChange={(v) => setRecurringForm(p => ({ ...p, time_slot: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {timeSlots.map(ts => <SelectItem key={ts.value} value={ts.value}>{t(ts.label)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Heures' : 'Hours'}</Label>
                <Select value={String(recurringForm.hours_requested)} onValueChange={(v) => setRecurringForm(p => ({ ...p, hours_requested: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[3, 4, 5, 6, 8, 10].map(h => <SelectItem key={h} value={String(h)}>{h}h</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRecurringDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="submit-recurring-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Repeat className="h-4 w-4 mr-2" />{language === 'fr' ? 'Créer' : 'Create'}</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Packages Dialog */}
      <Dialog open={packageDialogOpen} onOpenChange={setPackageDialogOpen}>
        <DialogContent className="sm:max-w-[700px]" data-testid="packages-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('packages')}</DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Choisissez un forfait adapté à vos besoins'
                : 'Choose a package that suits your needs'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid sm:grid-cols-2 gap-4 py-4">
            {Object.entries(packages).map(([key, pkg]) => (
              <Card key={key} className={`card-base ${key === 'pack_20' ? 'border-primary ring-2 ring-primary/20' : ''}`}>
                <CardContent className="p-5">
                  {key === 'pack_20' && (
                    <div className="badge-success mb-3 inline-block">
                      {language === 'fr' ? 'Meilleure offre' : 'Best value'}
                    </div>
                  )}
                  <h3 className="font-heading font-semibold text-lg">
                    {language === 'fr' ? pkg.name_fr : pkg.name_en}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {language === 'fr' ? pkg.description_fr : pkg.description_en}
                  </p>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-foreground">{pkg.price}€</span>
                    {pkg.hours && <span className="text-muted-foreground ml-1">/ {pkg.hours}h</span>}
                  </div>
                  <Button 
                    className="w-full mt-4 btn-primary"
                    onClick={() => handleBuyPackage(key)}
                    disabled={saving}
                    data-testid={`buy-package-${key}`}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        {t('payByCard')}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm text-muted-foreground text-center">
              {language === 'fr' 
                ? 'Paiement sécurisé par Stripe. Vous pouvez aussi payer par virement bancaire.'
                : 'Secure payment via Stripe. You can also pay by bank transfer.'
              }
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesPage;
